/** @jsxImportSource react */
import { useEffect, useMemo, useReducer, useState } from 'react'
import { Alert, Box, Breadcrumbs, Button, Chip, Link, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, closestCenter, pointerWithin, useSensor, useSensors,
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import { formsAPI } from '../../api/crm'
import { useFieldTypes, useTenantKey } from '../../hooks/useCrmQueries'
import { builderReducer, findFieldLocation, initialBuilderState } from '../../components/formBuilder/builderReducer'
import { fieldTypeLabel, fromResponse, hasErrors, mapServerErrors, toRequest, validateLocally } from '../../components/formBuilder/builderModel'
import FieldPalette, { PALETTE_PREFIX } from '../../components/formBuilder/FieldPalette'
import BuilderCanvas, { FieldCardBody, SECTION_PREFIX } from '../../components/formBuilder/BuilderCanvas'
import SettingsPanel from '../../components/formBuilder/SettingsPanel'
import FieldTypeIcon from '../../components/formBuilder/FieldTypeIcon'
import { getErrorList, getErrorMessage } from '../../utils/apiErrors'
import { formatDate } from '../../utils/helpers'
import { useNotify } from '../../components/common/NotificationProvider'
import Loading from '../../components/common/Loading'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import { SKIP_GUARD, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'

const NO_ERRORS = { meta: {}, items: {}, general: [] }
const STATUS_COLOR = { DRAFT: 'warning', PUBLISHED: 'success', ARCHIVED: 'default' }

// Fields win over the section container they sit in, so drops land at a precise position
const collisionDetection = (args) => {
  const hits = pointerWithin(args)
  if (hits.length) {
    const field = hits.find((h) => !String(h.id).startsWith(SECTION_PREFIX))
    return field ? [field] : hits
  }
  return closestCenter(args)
}

const FormBuilderPage = () => {
  const { formId } = useParams()
  const tenantId = useTenantKey()
  const { data, isLoading, error } = useQuery({
    queryKey: [tenantId, 'forms', 'one', formId],
    queryFn: () => formsAPI.get(formId),
    enabled: !!formId,
  })
  if (formId && isLoading) return <Loading />
  if (error) return <Alert severity="error">{getErrorMessage(error)}</Alert>
  return <FormBuilder key={formId || 'new'} saved={data} />
}

const FormBuilder = ({ saved }) => {
  const navigate = useNavigate()
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { data: fieldTypes } = useFieldTypes()
  const [state, dispatch] = useReducer(builderReducer, saved, (s) =>
    s ? { ...initialBuilderState, ...fromResponse(s) } : initialBuilderState)
  const [errors, setErrors] = useState(NO_ERRORS)
  const [busy, setBusy] = useState(null)
  const [dragging, setDragging] = useState(null)
  const [versionsAnchor, setVersionsAnchor] = useState(null)
  const [confirm, setConfirm] = useState(null)

  const isNew = !state.meta.id
  const readOnly = !isNew && state.meta.status !== 'DRAFT'
  const savedKeys = useMemo(() => new Set((saved?.sections || []).flatMap((s) => s.fields.map((f) => f.fieldKey))), [saved])

  // the query result changes after every save; reload the editor from it
  useEffect(() => {
    if (saved) dispatch({ type: 'load', state: fromResponse(saved) })
  }, [saved])

  const unsavedDialog = useUnsavedChangesGuard(state.dirty, 'This form has unsaved changes. Leave without saving?')

  const { data: versions = [] } = useQuery({
    queryKey: [tenantId, 'forms', 'versions', state.meta.code],
    queryFn: () => formsAPI.versions(state.meta.code),
    enabled: !isNew,
  })

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const refreshFormQueries = (form) => {
    queryClient.setQueryData([tenantId, 'forms', 'one', form.id], form)
    queryClient.invalidateQueries({ queryKey: [tenantId, 'forms'], predicate: (q) => q.queryKey[2] !== 'one' || q.queryKey[3] !== form.id })
  }

  /** Validates, saves, and returns the saved form (or null). */
  const save = async () => {
    const local = validateLocally(state)
    if (hasErrors(local)) {
      setErrors({ ...local, general: [] })
      notify('Fix the highlighted problems first', 'error')
      return null
    }
    const sentState = state
    try {
      const body = toRequest(sentState)
      const form = isNew ? await formsAPI.create(body) : await formsAPI.update(sentState.meta.id, body)
      setErrors(NO_ERRORS)
      refreshFormQueries(form)
      if (isNew) navigate(`/forms/${form.id}`, { replace: true, ...SKIP_GUARD })
      return form
    } catch (e) {
      const list = getErrorList(e)
      const mapped = mapServerErrors(list, sentState)
      setErrors({ ...mapped, general: list.length ? mapped.general : [getErrorMessage(e)] })
      notify(list.length ? 'The form has problems; see highlighted items' : getErrorMessage(e), 'error')
      return null
    }
  }

  const run = async (name, fn) => {
    setBusy(name)
    try {
      await fn()
    } finally {
      setBusy(null)
    }
  }

  const handleSave = () => run('save', async () => {
    const form = await save()
    if (form) notify('Draft saved')
  })

  const handlePublish = () => run('publish', async () => {
    const form = state.dirty || isNew ? await save() : { id: state.meta.id }
    if (!form) return
    try {
      const published = await formsAPI.publish(form.id)
      refreshFormQueries(published)
      notify(`Version ${published.version} is live`)
    } catch (e) {
      notify(getErrorMessage(e), 'error')
    }
  })

  const handleNewVersion = () => run('version', async () => {
    try {
      const draft = await formsAPI.newVersion(state.meta.id)
      queryClient.invalidateQueries({ queryKey: [tenantId, 'forms'] })
      navigate(`/forms/${draft.id}`)
      notify(`Draft version ${draft.version} created`)
    } catch (e) {
      notify(getErrorMessage(e), 'error')
    }
  })

  const confirmAction = () => run('confirm', async () => {
    try {
      if (confirm === 'archive') {
        const archived = await formsAPI.archive(state.meta.id)
        refreshFormQueries(archived)
        notify('Form archived; it no longer accepts new records')
      } else {
        await formsAPI.remove(state.meta.id)
        queryClient.invalidateQueries({ queryKey: [tenantId, 'forms'] })
        notify('Form deleted')
        navigate('/forms', SKIP_GUARD)
      }
    } catch (e) {
      notify(getErrorMessage(e), 'error')
    } finally {
      setConfirm(null)
    }
  })

  const addField = (fieldType) => {
    const sel = state.selected
    let sectionUid
    if (sel.kind === 'section') sectionUid = sel.uid
    if (sel.kind === 'field') sectionUid = findFieldLocation(state.sections, sel.uid)?.section.uid
    dispatch({ type: 'addField', fieldType, sectionUid })
  }

  const onDragStart = ({ active }) => setDragging(String(active.id))
  const onDragEnd = ({ active, over }) => {
    setDragging(null)
    if (!over) return
    const activeId = String(active.id)
    const overId = String(over.id)
    let toSectionUid
    let toIndex
    if (overId.startsWith(SECTION_PREFIX)) {
      toSectionUid = overId.slice(SECTION_PREFIX.length)
      toIndex = state.sections.find((s) => s.uid === toSectionUid)?.fields.length ?? 0
    } else {
      const location = findFieldLocation(state.sections, overId)
      if (!location) return
      toSectionUid = location.section.uid
      toIndex = location.index
    }
    if (activeId.startsWith(PALETTE_PREFIX)) {
      dispatch({ type: 'addField', fieldType: activeId.slice(PALETTE_PREFIX.length), sectionUid: toSectionUid, index: toIndex })
    } else if (activeId !== overId) {
      dispatch({ type: 'moveField', uid: activeId, toSectionUid, toIndex })
    }
  }

  const overlay = () => {
    if (!dragging) return null
    if (dragging.startsWith(PALETTE_PREFIX)) {
      const type = dragging.slice(PALETTE_PREFIX.length)
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: 1, bgcolor: 'background.paper', border: 1, borderColor: 'primary.main', boxShadow: 6 }}>
          <FieldTypeIcon type={type} color="primary" />
          <Typography variant="body2">{fieldTypeLabel(type)}</Typography>
        </Box>
      )
    }
    const location = findFieldLocation(state.sections, dragging)
    return location ? <FieldCardBody field={location.section.fields[location.index]} dragging /> : null
  }

  const live = versions.find((v) => v.status === 'PUBLISHED')

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to="/forms" underline="hover" color="inherit">Forms</Link>
        <Typography color="text.primary" noWrap>{state.meta.name || 'New form'}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, mb: 2 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h4" noWrap>{state.meta.name || 'New form'}</Typography>
            {!isNew && <Chip label={`v${state.meta.version} · ${state.meta.status.toLowerCase()}`} color={STATUS_COLOR[state.meta.status]} variant="outlined" />}
            {state.dirty && <Chip label="Unsaved" variant="outlined" />}
          </Stack>
          {live && live.id !== state.meta.id && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Live version: v{live.version}</Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {!isNew && (
            <Button startIcon={<HistoryIcon />} onClick={(e) => setVersionsAnchor(e.currentTarget)}>Versions</Button>
          )}
          {state.meta.status === 'PUBLISHED' && <Button color="inherit" onClick={() => setConfirm('archive')} disabled={!!busy}>Archive</Button>}
          {!isNew && state.meta.status !== 'PUBLISHED' && <Button color="error" onClick={() => setConfirm('delete')} disabled={!!busy}>Delete</Button>}
          {readOnly ? (
            <Button variant="contained" onClick={handleNewVersion} disabled={!!busy || versions.some((v) => v.status === 'DRAFT')}>
              Edit as new version
            </Button>
          ) : (
            <>
              <Button variant="outlined" onClick={handleSave} disabled={!!busy || (!isNew && !state.dirty)}>
                {busy === 'save' ? 'Saving…' : 'Save draft'}
              </Button>
              <Button variant="contained" onClick={handlePublish} disabled={!!busy}>
                {busy === 'publish' ? 'Publishing…' : 'Publish'}
              </Button>
            </>
          )}
        </Stack>
      </Box>

      <Menu anchorEl={versionsAnchor} open={!!versionsAnchor} onClose={() => setVersionsAnchor(null)}>
        {versions.map((v) => (
          <MenuItem key={v.id} selected={v.id === state.meta.id} onClick={() => { setVersionsAnchor(null); navigate(`/forms/${v.id}`) }}>
            <ListItemText primary={`Version ${v.version} · ${v.status.toLowerCase()}`} secondary={formatDate(v.updatedOn)} />
          </MenuItem>
        ))}
      </Menu>

      {readOnly && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {state.meta.status === 'PUBLISHED'
            ? 'This version is live and can’t be edited. “Edit as new version” makes an editable draft; records keep using this version until the draft is published.'
            : 'This version is archived and read-only.'}
          {versions.some((v) => v.status === 'DRAFT') && ' A draft version already exists; open it from Versions.'}
        </Alert>
      )}
      {errors.general.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors({ ...errors, general: [] })}>
          {errors.general.join(' · ')}
        </Alert>
      )}

      <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
        <Box sx={{ display: 'grid', gap: 2, alignItems: 'start', gridTemplateColumns: { xs: '1fr', lg: '210px minmax(0, 1fr) 360px' } }}>
          <Box sx={{ position: { lg: 'sticky' }, top: { lg: 64 } }}>
            <FieldPalette disabled={readOnly} onAdd={addField} />
          </Box>
          <BuilderCanvas state={state} errors={errors} readOnly={readOnly} dispatch={dispatch} />
          <Box sx={{ position: { lg: 'sticky' }, top: { lg: 64 }, maxHeight: { lg: 'calc(100vh - 80px)' }, overflowY: { lg: 'auto' } }}>
            <SettingsPanel state={state} isNew={isNew} readOnly={readOnly} errors={errors} fieldTypes={fieldTypes} savedKeys={savedKeys} dispatch={dispatch} />
          </Box>
        </Box>
        <DragOverlay dropAnimation={null}>{overlay()}</DragOverlay>
      </DndContext>

      {unsavedDialog}
      <ConfirmDialog
        open={!!confirm}
        title={confirm === 'archive' ? 'Archive this form?' : 'Delete this version?'}
        message={confirm === 'archive'
          ? 'No new records can be created or saved with it until a new version is published. Existing records are kept.'
          : 'This version will be removed. Other versions are not affected.'}
        confirmLabel={confirm === 'archive' ? 'Archive' : 'Delete'}
        destructive={confirm === 'delete'}
        busy={busy === 'confirm'}
        onConfirm={confirmAction}
        onClose={() => setConfirm(null)}
      />
    </Box>
  )
}

export default FormBuilderPage
